import { PortalBranding } from '../../../pure-js/libs/PortalBrandingHelper'

/**
 * Last-known portal branding per hostname, so the portal can paint a client's colors on the very
 * first render instead of after two sequential async hops (hostname → clientId → client document).
 *
 * Deliberately has no TTL: every load revalidates against Firestore and rewrites this entry, so a
 * stale value can survive exactly one render after an admin changes the config — an accepted
 * one-time flicker rather than a reason to re-add the flash on every visit.
 */
const CACHE_KEY_PREFIX = 'portal_branding_v1_'

/** Bare `localStorage` access throws outright in some privacy modes, so even reading it is guarded. */
const getStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

const isPortalBranding = (value: unknown): value is PortalBranding => {
  if (typeof value !== 'object' || value === null) return false

  const candidate = value as Partial<PortalBranding>
  return (
    typeof candidate.name === 'string' &&
    typeof candidate.primaryColor === 'string' &&
    typeof candidate.primaryColorRgb === 'string' &&
    typeof candidate.primaryColorDark === 'string' &&
    typeof candidate.primaryColorLight === 'string' &&
    typeof candidate.primaryColorSurface === 'string' &&
    typeof candidate.primaryColorSurfaceStrong === 'string' &&
    (candidate.logoUrl === undefined || typeof candidate.logoUrl === 'string')
  )
}

/**
 * Keyed by hostname even though `localStorage` is already per-origin: a dev machine can point
 * several hostnames at one origin (e.g. mapping `localhost` to a client while testing), and one
 * client's branding leaking into another's preview would be a confusing thing to debug.
 */
export const getPortalBrandingCacheKey = (hostname: string): string => `${CACHE_KEY_PREFIX}${hostname}`

export const readCachedPortalBranding = (hostname: string): PortalBranding | undefined => {
  const storage = getStorage()
  if (!storage || !hostname) return undefined

  try {
    const raw = storage.getItem(getPortalBrandingCacheKey(hostname))
    if (!raw) return undefined

    const parsed: unknown = JSON.parse(raw)
    // A shape written by an older build is discarded rather than rendered — the portal falls back
    // to Coursely defaults for that one load and the correct value is written straight after.
    return isPortalBranding(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

export const writeCachedPortalBranding = (hostname: string, branding: PortalBranding): void => {
  const storage = getStorage()
  if (!storage || !hostname) return

  try {
    const key = getPortalBrandingCacheKey(hostname)
    const serialized = JSON.stringify(branding)
    // Skip no-op writes: this runs on every settled render, and an unchanged entry is the norm.
    if (storage.getItem(key) === serialized) return

    storage.setItem(key, serialized)
  } catch {
    // A full or unavailable quota only costs the flash-free first paint; never break the portal.
  }
}
